import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Shield, AlertTriangle, CreditCard, FlaskConical, Wind, Search, FileText, CheckCircle2, Gavel, Timer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* Stop — /cef/stop/:serverId/:stopId — the Real Cops traffic-stop / street-encounter
   surface. Transparent overlay: the cruiser and officer are in the 3D world; this is
   the encounter chrome. The hub runs the state machine — every action here is a real
   server decision (searches read your actual pockets, fines hit your actual bank). */

const STATUS_STYLE = {
  active: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  warning_issued: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  fined: "text-nc-orange border-nc-orange/40 bg-nc-orange/10",
  arrested: "text-red-400 border-red-400/40 bg-red-400/10",
  fled: "text-red-300 border-red-300/40 bg-red-300/10",
};

const ACTION_META = {
  show_id: { label: "Hand over ID", icon: FileText },
  refuse_id: { label: "Refuse to identify", icon: AlertTriangle },
  consent_search: { label: "Consent to search", icon: Search },
  refuse_search: { label: "Refuse search", icon: AlertTriangle },
  breath_test: { label: "Take breath test", icon: Wind },
  drug_test: { label: "Take drug test", icon: FlaskConical },
  pay_fine: { label: "Pay fine", icon: CreditCard },
  accept_warning: { label: "Accept warning", icon: CheckCircle2 },
  flee: { label: "FLEE", icon: AlertTriangle },
};

export default function StopCefPage() {
  const { serverId, stopId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [stop, setStop] = useState(null);
  const [note, setNote] = useState("");
  const [copLine, setCopLine] = useState("");
  const [jail, setJail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (demo) {
      setStop({ id: "STP-DEMO01", status: "active", stage: "greet", kind: "traffic",
        reason: "Excessive speed — 95 in a 50 zone", fine_amount: 350,
        cop: { name: "Renee Holloway", badge: "4471", rank: "Senior Constable", personality: "by_the_book",
               desc: "follows procedure to the letter, no warnings" },
        checks: { id_shown: false, search: null, breath: null, drug: null }, findings: [],
        available_actions: ["show_id", "refuse_id", "flee"], bank: 5000 });
      setCopLine("License and registration. You know why I stopped you.");
      return;
    }
    try {
      const r = await api.get(`/nc/servers/${serverId}/cops/stop/${stopId}`, { params: { token } });
      setStop(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Stop not found"); }
  }, [serverId, stopId, token, demo]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    if (busy) return;
    if (demo) {
      setNote("Preview mode — the real surface talks to the live city.");
      return;
    }
    setBusy(true);
    setNote(""); setCopLine("");
    try {
      const r = await api.post(`/nc/servers/${serverId}/cops/stop/${stopId}/action`, { token, action });
      setStop(r.data);
      if (r.data.message) setNote(r.data.message);
      if (r.data.cop_line) setCopLine(r.data.cop_line);
      if (r.data.status === "arrested") {
        const j = await api.get(`/nc/servers/${serverId}/cops/jail`, { params: { token } });
        setJail(j.data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "That didn't work"); }
    setBusy(false);
  };

  if (!stop) return <div className="fixed inset-0 bg-transparent" />;

  const s = stop;
  const active = s.status === "active";

  return (
    <div data-testid="stop-page" className="fixed inset-0 bg-transparent flex items-center justify-end pr-[4vw] pointer-events-none">
      {demo && <PreviewRibbon />}
      <div className="w-full max-w-md flex flex-col gap-3 pointer-events-auto" style={{ animation: "ncFadeUp 0.5s ease both" }}>
        {/* officer card */}
        <div data-testid="stop-cop-card" className="bg-black/80 backdrop-blur-md border border-white/15 p-4 flex items-center gap-4">
          <div className="w-11 h-11 rounded-full bg-nc-blue/15 border border-nc-blue/50 flex items-center justify-center shrink-0">
            <Shield className="w-5 h-5 text-nc-blue" />
          </div>
          <div className="min-w-0">
            <p className="font-sans font-semibold text-sm text-zinc-100">{s.cop.rank} {s.cop.name}</p>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">badge {s.cop.badge} · {s.cop.personality.replace(/_/g, " ")} · {s.cop_arc_stage || "green"}</p>
            <p className="font-mono text-[9px] text-zinc-600 mt-0.5 italic">{s.cop.desc}</p>
            {s.grudge && (
              <p data-testid="stop-grudge" className="font-mono text-[8px] tracking-[0.2em] uppercase mt-1 text-red-400 animate-pulse">
                personal grudge — you ran from this officer
              </p>
            )}
            {s.recognition && s.recognition !== "neutral" && (
              <p data-testid="stop-recognition" className={`font-mono text-[8px] tracking-[0.2em] uppercase mt-1 ${
                s.recognition === "hostile" ? "text-red-400" : s.recognition === "trusted" ? "text-emerald-300" : "text-amber-300"}`}>
                {s.recognition === "hostile" ? "knows your face — repeat offender"
                 : s.recognition === "trusted" ? "knows you — model citizen"
                 : "remembers you"}
              </p>
            )}
          </div>
          <span data-testid="stop-status" className={`ml-auto font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 shrink-0 ${STATUS_STYLE[s.status] || STATUS_STYLE.active}`}>
            {s.status.replace(/_/g, " ")}
          </span>
        </div>

        {/* encounter body */}
        <div className="bg-black/75 backdrop-blur-md border border-white/10 p-4 flex flex-col gap-3">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-nc-orange flex items-center gap-2">
            {s.kind === "traffic" ? "Traffic stop" : "Street encounter"}
          </p>
          <p data-testid="stop-reason" className="font-mono text-xs text-zinc-300">{s.reason}</p>
          {copLine && <p data-testid="stop-cop-line" className="font-mono text-xs text-sky-200 bg-nc-blue/10 border border-nc-blue/25 px-3 py-2 leading-relaxed">"{copLine}"</p>}
          {note && <p data-testid="stop-note" className="font-mono text-xs text-zinc-200 leading-relaxed border-l-2 border-nc-orange pl-3">{note}</p>}

          {/* ID card render once shown */}
          {s.checks.id_shown && (
            <div data-testid="stop-id-card" className="border border-white/20 bg-[#0a0d14] p-3 flex items-center gap-3" style={{ animation: "ncFadeUp 0.4s ease both" }}>
              <div className="w-9 h-11 bg-nc-blue/20 border border-nc-blue/40 flex items-center justify-center">
                <FileText className="w-4 h-4 text-nc-blue" />
              </div>
              <div>
                <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase">Bornwood citizen ID</p>
                <p className="font-sans font-semibold text-sm text-zinc-100">Verified with dispatch</p>
                <p className="font-mono text-[9px] text-emerald-300 uppercase tracking-[0.15em]">record clean</p>
              </div>
            </div>
          )}

          {/* findings + test results */}
          {s.checks.breath != null && (
            <p data-testid="stop-breath" className="font-mono text-[10px] text-zinc-400">Breathalyser: <span className={s.checks.breath >= 0.05 ? "text-red-400" : "text-emerald-300"}>BAC {Number(s.checks.breath).toFixed(3)}</span></p>
          )}
          {s.checks.drug && (
            <p data-testid="stop-drug" className="font-mono text-[10px] text-zinc-400">Saliva test: <span className={s.checks.drug === "positive" ? "text-red-400" : "text-emerald-300"}>{s.checks.drug}</span></p>
          )}
          {s.findings.length > 0 && (
            <div data-testid="stop-findings" className="border border-red-400/30 bg-red-400/5 p-3">
              <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-red-400 mb-1.5">Seized</p>
              {s.findings.map((f, i) => <p key={i} className="font-mono text-[10px] text-zinc-300">{f}</p>)}
            </div>
          )}
          {s.checks.search === "clean" && <p data-testid="stop-search-clean" className="font-mono text-[10px] text-emerald-300">Search: nothing found.</p>}
          {s.checks.search === "refused" && <p data-testid="stop-search-refused" className="font-mono text-[10px] text-zinc-400">Search refused — no grounds to override.</p>}
        </div>

        {/* actions */}
        {active && (
          <div data-testid="stop-actions" className="flex flex-wrap gap-2">
            {s.available_actions.map((a) => {
              const meta = ACTION_META[a] || { label: a, icon: FileText };
              const Icon = meta.icon;
              const label = a === "pay_fine" ? `Pay fine — $${s.fine_amount.toLocaleString()}` : meta.label;
              const danger = a === "flee" || a.startsWith("refuse");
              return (
                <button key={a} data-testid={`stop-action-${a}`} onClick={() => act(a)} disabled={busy}
                        className={`font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors duration-200 flex items-center gap-2 disabled:opacity-40
                          ${danger ? "border-red-400/40 text-red-300 hover:bg-red-400/10"
                                   : "border-white/20 text-zinc-200 hover:border-nc-blue hover:text-nc-blue"}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
          </div>
        )}

        {/* outcomes */}
        {!active && (
          <div data-testid="stop-outcome" className="bg-black/80 backdrop-blur-md border border-white/15 p-4 flex flex-col gap-2">
            {s.status === "warning_issued" && <p className="font-mono text-xs text-emerald-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Warning issued. You're free to go.</p>}
            {s.status === "fined" && <p className="font-mono text-xs text-nc-orange flex items-center gap-2"><CreditCard className="w-4 h-4" /> Fine paid — ${s.fine_amount.toLocaleString()}. Ticket closed.</p>}
            {s.status === "arrested" && (
              <div>
                <p className="font-mono text-xs text-red-400 flex items-center gap-2"><Gavel className="w-4 h-4" /> Under arrest. Transport to holding.</p>
                {jail?.jailed && (
                  <p data-testid="stop-jail-timer" className="font-mono text-[10px] text-zinc-400 mt-1.5 flex items-center gap-2">
                    <Timer className="w-3.5 h-3.5" /> Cell time: {Math.ceil(jail.seconds_left / 60)} min — {jail.reason}
                  </p>
                )}
              </div>
            )}
            {s.status === "fled" && <p className="font-mono text-xs text-red-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> You fled. A 10-80 is live on every police MDT — lay low.</p>}
            <button data-testid="stop-close" onClick={() => window.close()}
                    className="self-start mt-1 font-mono text-[9px] uppercase tracking-[0.2em] border border-white/20 hover:border-white/50 text-zinc-400 px-4 py-2 transition-colors">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
